import { useEffect, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { motion, AnimatePresence } from 'framer-motion'

import { Input, SecondaryButton } from 'ui'
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogOverlay,
  DialogTitle,
} from './Dialog'
import Banner from './Navbar/Banner'
import { useToast } from './Navbar/Toast'
import { TransCompatLink } from './Navbar'

import { useSubmitHome } from '../services/listing/submitHome'

import { CheckIcon } from 'icons'

import useAuth from '../hooks/useAuth'

const SubmitHomeDialog = () => {
  const { t } = useTranslation(['homes', 'common'])
  const { setToast } = useToast()
  const { mutate: submitHome, status: submitHomeStatus } = useSubmitHome()
  const router = useRouter()
  const { submitHome: isOpenQueryParam } = router.query
  
  const {
    user
  } = useAuth()

  const [isOpen, setIsOpen] = useState<boolean>(!!isOpenQueryParam)
  const [address, setAddress] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState(false)

  const handleClose = () => {
    setIsOpen(false)
    const updatedQuery = { ...router.query }
    delete updatedQuery['submitHome']
    router.replace({ pathname: router.pathname, query: updatedQuery}, undefined, { scroll: false })
  }

  useEffect(() => {
    setIsOpen(!!isOpenQueryParam)
  }, [isOpenQueryParam])

  useEffect(() => {
    if (user) { setEmail(user.email) }
  }, [user])

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <DialogOverlay>
          <form
            className="w-[min(500px,95%)]"
            onSubmit={(e) => {
              e.preventDefault()
              setError(false)
              submitHome(
                { address, email },
                {
                  onSuccess: () => {
                    handleClose()
                    setAddress('')
                    setToast({
                      message: `${address} was submitted`,
                      icon: CheckIcon,
                    })
                  },
                  onError: () => {
                    setError(true)
                  },
                }
              )
            }}
          >
            <DialogContent className="relative overflow-hidden">
              <DialogTitle>{t('submit-home.title')}</DialogTitle>
              <DialogBody className="pb-60" noMinHeight>
                <p className="mt-4">
                  {t('')}
                  <Trans i18nKey="submit-home.description">
                    Submit a home to see if it qualifies for purchase with Acre. We&apos;ll let you know via email. See what{' '} 
                    <TransCompatLink
                      href="https://discover.acrehomes.com/blog-posts/only-the-best-homes-are-acre-certified"
                      className="text-teal-4"
                    >
                      makes an Acre home.
                    </TransCompatLink>
                  </Trans>
                </p>
                <Input
                  className="mt-32"
                  id="address"
                  label={t('submit-home.address')}
                  placeholder={t('submit-home.address')}
                  value={address}
                  required
                  customOnChange={(id, value) => setAddress(value)}
                />
                {!user ? (
                  <Input
                    className="mt-32"
                    id="email"
                    label={t('submit-home.email')}
                    placeholder={t('submit-home.email')}
                    value={email}
                    required
                    customOnChange={(id, value) => setEmail(value)}
                  />
                ) : null}
              </DialogBody>
              <div className="relative z-0">
                <DialogFooter className="bg-white">
                  <SecondaryButton
                    type="submit"
                    disabled={!address || !email}
                    loading={submitHomeStatus === 'loading'}
                  >
                    {t('ui.submit', { ns: 'common' })}
                  </SecondaryButton>
                </DialogFooter>
                <AnimatePresence>
                  {error ? (
                    <motion.div
                      initial={{ y: '100%' }}
                      animate={{ y: 0 }}
                      exit={{ y: '100%' }}
                      className="absolute left-0 bottom-full -z-10 w-full"
                    >
                      <Banner error>
                        {t('ui.submit-error', { ns: 'common' })}
                      </Banner>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </DialogContent>
          </form>
        </DialogOverlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default SubmitHomeDialog
